/**
 * @file api.js
 * @description Centralized HTTP client service for MemoTrack SAAS.
 * Handles multi-tenant request resolution, authorization headers, in-flight request deduplication,
 * session expiration redirects, and API endpoints for auth, hospital infrastructure, memos,
 * and user activities.
 */

import { AppConfig, subscribeToRemoteConfig } from '../config/remoteConfig';

/**
 * ApiService provides unified networking utilities and typed endpoints across MemoTrack modules.
 */
class ApiService {
  /**
   * Initializes the ApiService instance with the active backend base URL and request caches.
   */
  constructor() {
    // this.baseURL = 'http://localhost:8000';
    // this.baseURL = 'https://app.memotrack.net';
    this.baseURL = AppConfig.backendUrl;
    this.inFlightRequests = new Map();
    this.unsubscribeFromConfig = subscribeToRemoteConfig((config) => {
        this.baseURL = AppConfig.backendUrl;
    });
  }

  /**
   * Dispatches an HTTP request with multi-tenant headers, auth token, and request deduplication.
   *
   * @param {string} endpoint - API path or full external URL.
   * @param {Object} [options={}] - Request options including method, headers, body, isBlob, skipAuth.
   * @returns {Promise<any>} Parsed JSON response payload or Blob.
   * @throws {Error} When network or HTTP error occurs.
   */
  async request(endpoint, options = {}) {
    const url = endpoint.startsWith('http://') || endpoint.startsWith('https://')
      ? endpoint
      : `${this.baseURL}${endpoint}`;

    const headers = {
      ...(options.isBlob ? {} : { 'Content-Type': 'application/json' }),
      ...(options.headers || {}),
    };

    const token = localStorage.getItem('authToken');
    if (token && !options.skipAuth) {
      headers.Authorization = `Token ${token}`;
    }

    const config = {
      ...options,
      headers,
    };

    const getTenantSlug = () => {
      try {
        const hospitalStr = localStorage.getItem('hospital');
        if (hospitalStr) {
          const hospital = JSON.parse(hospitalStr);
          if (hospital.schema_name) {
            localStorage.setItem('tenant_slug', hospital.schema_name);
            return hospital.schema_name;
          }
        }
      } catch (e) { }

      let slug = localStorage.getItem('tenant_slug');
      if (slug) return slug;

      const hostname = window.location.hostname;
      const parts = hostname.split('.');
      if (parts.length >= 3) {
        slug = parts[0]; // e.g. apollo.memotrack.net -> apollo
      } else if (parts.length === 2 && parts[1] === 'localhost') {
        slug = parts[0]; // e.g. apollo.localhost -> apollo
      } else {
        slug = parts[0] === 'localhost' ? '' : parts[0];
      }

      if (slug) {
        localStorage.setItem('tenant_slug', slug);
      }

      return slug;
    };

    const tenantSlug = getTenantSlug();
    if (tenantSlug) {
      config.headers['X-Tenant'] = tenantSlug;
    }

    const isGet = !options.method || options.method.toUpperCase() === 'GET';
    if (!isGet) {
      this.inFlightRequests.clear();
    }
    const isRefresh = options.forceRefresh || (typeof url === 'string' && url.includes('refresh=true'));
    const reqKey = isGet && !options.isBlob && !isRefresh ? `${url}_${JSON.stringify(config.headers)}` : null;

    if (reqKey && this.inFlightRequests.has(reqKey)) {
      return this.inFlightRequests.get(reqKey);
    }

    const fetchPromise = (async () => {
      try {
        const response = await fetch(url, config);
        // Session expired / invalid token or forbidden
        if (response.status === 401 || response.status === 403) {
          let errorMsg = "Your session expired. Please login again.";
          if (response.status === 403) {
            try {
              const errorData = await response.json();
              errorMsg = errorData.error || "Access denied. Please check your permissions.";
            } catch (e) { }
          }

          // Clear local auth data
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');

          // Show message
          alert(errorMsg);

          // Redirect login
          window.location.href = '/';
          return;
        }

        if (!response.ok) {
          let errorMsg = `HTTP error! status: ${response.status}`;
          try {
            const errorData = await response.json();
            errorMsg = errorData.error || errorData.detail || errorData.message || errorMsg;
          } catch (e) { }
          throw new Error(errorMsg);
        }

        if (options.isBlob) {
          return await response.blob();
        }

        if (response.status === 204) {
          return null;
        }

        return await response.json();
      } catch (error) {
        console.error('API request failed:', error);
        throw error;
      } finally {
        if (reqKey) {
          this.inFlightRequests.delete(reqKey);
        }
      }
    })();

    if (reqKey) {
      this.inFlightRequests.set(reqKey, fetchPromise);
    }

    return fetchPromise;
  }

  // ================= Auth endpoints =================

  /**
   * Authenticate a hospital staff user via phone number and MPIN.
   *
   * @param {string} phoneNumber - User's 10-digit registered phone number.
   * @param {string} mpin - 6-digit MPIN credentials.
   * @param {string|null} [fcmToken=null] - Optional Firebase Cloud Messaging token.
   * @returns {Promise<Object>} Object containing auth token, user details, and hospital context.
   */
  async login(phoneNumber, mpin, fcmToken = null) {
    try {
      const body = {
        phone: phoneNumber,
        mpin: mpin
      };

      if (fcmToken) {
        body['fcm_token'] = fcmToken;
      }

      const data = await this.request("/api/token-auth/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      return data;
    } catch (error) {
      console.error("Login failed:", error);
      throw error;
    }
  }

  /**
   * Invalidate current user token and clear all local credentials and tenant preferences.
   *
   * @returns {Promise<void>}
   */
  async logout() {
    if (this.isLoggingOut) return;
    this.isLoggingOut = true;
    try {
      const token = localStorage.getItem("authToken");

      if (token) {
        await this.request('/api/token-auth/logout/', {
          method: 'POST',
        });
      }
    } catch (error) {
      console.warn("Logout API failed (ignored):", error);
    } finally {
      localStorage.removeItem("authToken");
      localStorage.removeItem("user");
      localStorage.removeItem("hospital");
      localStorage.removeItem('tenant_slug');
      this.isLoggingOut = false;
    }
  }

  /**
   * Request an OTP for password or MPIN reset.
   *
   * @param {string} phoneNumber - Target user's phone number.
   * @param {string} newMpin - Proposed new MPIN to set upon verification.
   * @returns {Promise<Object>} API status confirmation response.
   */
  async send_otp(phoneNumber, newMpin) {
    return this.request('/api/send-otp/', {
      method: 'POST',
      body: JSON.stringify({
        phone: phoneNumber,
        mpin: newMpin,
      })
    });
  }

  /**
   * Verify an OTP and finalize the password/MPIN update.
   *
   * @param {string} phoneNumber - User's registered phone number.
   * @param {string} otp - Received 6-digit OTP code.
   * @returns {Promise<Object>} Verification status response.
   */
  async verifyOtp(phoneNumber, otp) {
    return this.request('/api/reset-password/', {
      method: 'POST',
      body: JSON.stringify({
        phone: phoneNumber,
        otp: otp,
      })
    });
  }

  // ================= Hospital & User Endpoints =================

  /**
   * Retrieve hospital metadata, boundary geometry, and configuration.
   *
   * @param {number|string} hospitalId - Target hospital identifier.
   * @returns {Promise<Object>} Hospital profile record.
   */
  async getHospital(hospitalId) {
    return this.request(`/api/hospitals/${hospitalId}/`);
  }

  /**
   * Fetch all staff members belonging to the specified hospital.
   *
   * @param {number|string} hospitalId - Hospital identifier.
   * @returns {Promise<Array<Object>>} List of staff user objects.
   */
  async getUsers(hospitalId) {
    return this.request(`/api/hospitals/${hospitalId}/users/`);
  }

  /**
   * Create and register a new staff user under a hospital tenant.
   *
   * @param {number|string} hospitalId - Hospital identifier.
   * @param {Object} userData - User record payload.
   * @returns {Promise<Object>} Created user record.
   */
  async createUser(hospitalId, userData) {
    return this.request(`/api/hospitals/${hospitalId}/users/`, {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  /**
   * Update existing staff user attributes.
   *
   * @param {number|string} hospitalId - Hospital identifier.
   * @param {number|string} userId - User identifier.
   * @param {Object} userData - Partial attributes to patch.
   * @returns {Promise<Object>} Updated user record.
   */
  async updateUser(hospitalId, userId, userData) {
    return this.request(`/api/hospitals/${hospitalId}/users/${userId}/`, {
      method: 'PATCH',
      body: JSON.stringify(userData),
    });
  }

  /**
   * Delete or deactivate a hospital staff user.
   *
   * @param {number|string} hospitalId - Hospital identifier.
   * @param {number|string} userId - User identifier to remove.
   * @returns {Promise<null>}
   */
  async deleteUser(hospitalId, userId) {
    return this.request(`/api/hospitals/${hospitalId}/users/${userId}/`, {
      method: 'DELETE',
    });
  }

  // ================= Roles Endpoints =================

  /**
   * Retrieve all functional staff roles for the specified hospital.
   *
   * @param {number|string} hospitalId - Hospital identifier.
   * @returns {Promise<Array<Object>>} List of hospital role definitions.
   */
  async getRoles(hospitalId) {
    return await this.request(`/api/hospitals/${hospitalId}/roles/`);
  }

  /**
   * Create a new custom role with approval/responder permissions.
   *
   * @param {number|string} hospitalId - Hospital identifier.
   * @param {Object} roleData - Role attributes payload.
   * @returns {Promise<Object>} Created role object.
   */
  async createRole(hospitalId, roleData) {
    return this.request(`/api/hospitals/${hospitalId}/roles/`, {
      method: 'POST',
      body: JSON.stringify(roleData),
    });
  }

  /**
   * Update an existing hospital role.
   *
   * @param {number|string} hospitalId - Hospital identifier.
   * @param {number|string} roleId - Role identifier.
   * @param {Object} roleData - Attributes to update.
   * @returns {Promise<Object>} Updated role object.
   */
  async updateRole(hospitalId, roleId, roleData) {
    return this.request(`/api/hospitals/${hospitalId}/roles/${roleId}/`, {
      method: 'PATCH',
      body: JSON.stringify(roleData),
    });
  }

  /**
   * Delete an existing hospital role.
   *
   * @param {number|string} hospitalId - Hospital identifier.
   * @param {number|string} roleId - Role identifier.
   * @returns {Promise<null>}
   */
  async deleteRole(hospitalId, roleId) {
    return this.request(`/api/hospitals/${hospitalId}/roles/${roleId}/`, {
      method: 'DELETE',
    });
  }

  // ================= Blocks Endpoints =================

  /**
   * Fetch all building blocks registered for a hospital.
   *
   * @param {number|string} hospitalId - Hospital identifier.
   * @returns {Promise<Array<Object>>} List of block records.
   */
  async getBlocks(hospitalId) {
    return this.request(`/api/hospitals/${hospitalId}/blocks/`);
  }

  /**
   * Retrieve specific hospital block details.
   *
   * @param {number|string} hospitalId - Hospital identifier.
   * @param {number|string} blockId - Block identifier.
   * @returns {Promise<Object>} Block record.
   */
  async getBlock(hospitalId, blockId) {
    return this.request(`/api/hospitals/${hospitalId}/blocks/${blockId}/`);
  }

  /**
   * Create a new building block.
   *
   * @param {number|string} hospitalId - Hospital identifier.
   * @param {Object} blockData - Block parameters including name and floor count.
   * @returns {Promise<Object>} Created block record.
   */
  async createBlock(hospitalId, blockData) {
    return this.request(`/api/hospitals/${hospitalId}/blocks/`, {
      method: 'POST',
      body: JSON.stringify(blockData),
    });
  }

  /**
   * Update building block properties.
   *
   * @param {number|string} hospitalId - Hospital identifier.
   * @param {number|string} blockId - Block identifier.
   * @param {Object} blockData - Updated attributes.
   * @returns {Promise<Object>} Updated block record.
   */
  async updateBlock(hospitalId, blockId, blockData) {
    return this.request(`/api/hospitals/${hospitalId}/blocks/${blockId}/`, {
      method: 'PATCH',
      body: JSON.stringify(blockData),
    });
  }

  /**
   * Delete a building block.
   *
   * @param {number|string} hospitalId - Hospital identifier.
   * @param {number|string} blockId - Block identifier.
   * @returns {Promise<null>}
   */
  async deleteBlock(hospitalId, blockId) {
    return this.request(`/api/hospitals/${hospitalId}/blocks/${blockId}/`, {
      method: 'DELETE',
    });
  }

  // ================= Shifts Endpoints =================

  /**
   * Retrieve all staff shift schedules for a hospital.
   *
   * @param {number|string} hospitalId - Hospital identifier.
   * @returns {Promise<Array<Object>>} List of shift records.
   */
  async getShifts(hospitalId) {
    return this.request(`/api/hospitals/${hospitalId}/shifts/`);
  }

  /**
   * Create a new shift time window.
   *
   * @param {number|string} hospitalId - Hospital identifier.
   * @param {Object} shiftData - Shift name, start_time, and end_time.
   * @returns {Promise<Object>} Created shift record.
   */
  async createShift(hospitalId, shiftData) {
    return this.request(`/api/hospitals/${hospitalId}/shifts/`, {
      method: 'POST',
      body: JSON.stringify(shiftData),
    });
  }

  /**
   * Update an existing shift schedule.
   *
   * @param {number|string} hospitalId - Hospital identifier.
   * @param {number|string} shiftId - Shift identifier.
   * @param {Object} shiftData - Updated shift attributes.
   * @returns {Promise<Object>} Updated shift record.
   */
  async updateShift(hospitalId, shiftId, shiftData) {
    return this.request(`/api/hospitals/${hospitalId}/shifts/${shiftId}/`, {
      method: 'PATCH',
      body: JSON.stringify(shiftData),
    });
  }

  /**
   * Delete a staff shift schedule.
   *
   * @param {number|string} hospitalId - Hospital identifier.
   * @param {number|string} shiftId - Shift identifier.
   * @returns {Promise<null>}
   */
  async deleteShift(hospitalId, shiftId) {
    return this.request(`/api/hospitals/${hospitalId}/shifts/${shiftId}/`, {
      method: 'DELETE',
    });
  }

  // ================= Approver Hierarchies =================

  /**
   * Retrieve configured multi-level approval hierarchies for a hospital.
   *
   * @param {number|string} hospitalId - Hospital identifier.
   * @returns {Promise<Array<Object>>} List of hierarchy configurations.
   */
  async getApproverHierarchies(hospitalId) {
    return this.request(`/api/hospitals/${hospitalId}/hierarchies/`);
  }

  /**
   * Create a new approval hierarchy chain.
   *
   * @param {number|string} hospitalId - Hospital identifier.
   * @param {Object} data - Approver hierarchy payload with nested levels.
   * @returns {Promise<Object>} Created hierarchy record.
   */
  async createApproverHierarchy(hospitalId, data) {
    return this.request(`/api/hospitals/${hospitalId}/hierarchies/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Update an approval hierarchy chain.
   *
   * @param {number|string} hospitalId - Hospital identifier.
   * @param {number|string} id - Hierarchy identifier.
   * @param {Object} data - Updated hierarchy properties.
   * @returns {Promise<Object>} Updated hierarchy record.
   */
  async updateApproverHierarchy(hospitalId, id, data) {
    return this.request(`/api/hospitals/${hospitalId}/hierarchies/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete an approval hierarchy.
   *
   * @param {number|string} hospitalId - Hospital identifier.
   * @param {number|string} id - Hierarchy identifier.
   * @returns {Promise<null>}
   */
  async deleteApproverHierarchy(hospitalId, id) {
    return this.request(`/api/hospitals/${hospitalId}/hierarchies/${id}/`, {
      method: 'DELETE',
    });
  }

  // ================= Memo Endpoints =================

  /**
   * Submit a new complaint memo.
   *
   * @param {number|string} hospitalId - Hospital identifier.
   * @param {Object} memoData - Complaint title, department, priority, and location metadata.
   * @returns {Promise<Object>} Newly created memo snapshot.
   */
  async createMemo(hospitalId, memoData) {
    return this.request(`/api/${hospitalId}/create-memo/`, {
      method: 'POST',
      body: JSON.stringify(memoData),
    });
  }

  /**
   * Partially modify an existing memo.
   *
   * @param {number|string} hospitalId - Hospital identifier.
   * @param {string} memoId - Unique UUID or ID of the memo.
   * @param {Object} memoData - Attributes to update (complaint, department, tagged_role).
   * @returns {Promise<Object>} Updated memo snapshot.
   */
  async updateMemo(hospitalId, memoId, memoData) {
    return this.request(`/api/${hospitalId}/memos/${memoId}/`, {
      method: 'PATCH',
      body: JSON.stringify(memoData),
    });
  }

  /**
   * Retrieve full details and event history for a single memo.
   *
   * @param {number|string} hospitalId - Hospital identifier.
   * @param {string} memoId - Unique memo identifier.
   * @returns {Promise<Object>} Latest memo snapshot with timeline events.
   */
  async getMemo(hospitalId, memoId) {
    return this.request(`/api/${hospitalId}/memos/${memoId}/`);
  }

  /**
   * Query list of memos filtered by user role, status, or search query.
   *
   * @param {number|string} hospitalId - Hospital identifier.
   * @param {string} [queryParams=''] - URL query parameter string.
   * @returns {Promise<Array<Object>>} List of matching memo records.
   */
  async getUserMemos(hospitalId, queryParams = '') {
    const query = queryParams ? `?${queryParams}` : '';
    return this.request(`/api/${hospitalId}/memos/${query}`);
  }

  /**
   * Approve a pending memo progression.
   *
   * @param {number|string} hospitalId - Hospital identifier.
   * @param {string} memoId - Memo identifier.
   * @param {Object} data - Approval notes and metadata.
   * @returns {Promise<Object>} Updated status response.
   */
  async approveMemo(hospitalId, memoId, data) {
    return this.request(`/api/${hospitalId}/memos/${memoId}/approve/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Reject a memo at the current approval hierarchy level.
   *
   * @param {number|string} hospitalId - Hospital identifier.
   * @param {string} memoId - Memo identifier.
   * @param {Object} data - Rejection reason and notes.
   * @returns {Promise<Object>} Updated status response.
   */
  async rejectMemo(hospitalId, memoId, data) {
    return this.request(`/api/${hospitalId}/memos/${memoId}/reject/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Export an individual memo report as an Excel spreadsheet binary blob.
   *
   * @param {string} memoId - Unique memo identifier.
   * @returns {Promise<Blob>} Excel workbook binary stream.
   */
  async exportSingleMemo(memoId) {
    return this.request(`/api/export/memo/${memoId}/`, {
      isBlob: true,
    });
  }

  /**
   * Export filtered hospital memos as an Excel spreadsheet binary blob.
   *
   * @param {number|string} hospitalId - Hospital identifier.
   * @param {Object} [params={}] - Filter parameters (date_from, date_to, department, status).
   * @returns {Promise<Blob>} Excel workbook binary stream.
   */
  async exportMemos(hospitalId, params = {}) {
    const queryParams = new URLSearchParams(params).toString();
    const endpoint = hospitalId
      ? `/api/export/memos/${hospitalId}/${queryParams ? `?${queryParams}` : ''}`
      : `/api/export/memos/${queryParams ? `?${queryParams}` : ''}`;
    return this.request(endpoint, {
      isBlob: true,
    });
  }

  /**
   * Fetch precomputed dashboard analytics JSON directly from static storage.
   *
   * @param {string} jsonUrl - Media URL of the precomputed JSON report.
   * @returns {Promise<Object>} Precomputed dashboard metrics payload.
   */
  async fetchDashboardJson(jsonUrl) {
    return this.request(jsonUrl, { skipAuth: true });
  }

  // =============== Worker Status =================

  /**
   * Log an attended or work completion status event with verification OTP.
   *
   * @param {number|string} hospitalId - Hospital identifier.
   * @param {string} memoId - Memo identifier.
   * @param {Object} data - Event payload with event_type, OTP, and comments.
   * @returns {Promise<Object>} Action response.
   */
  async addWorkerStatus(hospitalId, memoId, data) {
    return this.request(`/api/${hospitalId}/memos/${memoId}/work-status/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Remove a worker assignment or event record.
   *
   * @param {number|string} hospitalId - Hospital identifier.
   * @param {string} memoId - Memo identifier.
   * @param {number|string} eventId - Unique event identifier to delete.
   * @param {Object} [metadata={}] - Deletion audit context.
   * @returns {Promise<Object>} Action response.
   */
  async deleteWorkerStatus(hospitalId, memoId, eventId, metadata = {}) {
    return this.request(`/api/${hospitalId}/memos/${memoId}/work-status/`, {
      method: 'DELETE',
      body: JSON.stringify({
        event_type: 'worker_status_delete',
        payload: { event_id: eventId },
        metadata,
      }),
    });
  }

  // =============== Wards & Spatial Management =================

  /**
   * Fetch all wards configured across hospital blocks.
   *
   * @param {number|string} hospitalId - Hospital identifier.
   * @returns {Promise<Array<Object>>} List of ward records.
   */
  async getWards(hospitalId) {
    return this.request(`/api/hospitals/${hospitalId}/wards/`);
  }

  /**
   * Fetch wards belonging exclusively to a specific building block.
   *
   * @param {number|string} hospitalId - Hospital identifier.
   * @param {number|string} blockId - Block identifier.
   * @returns {Promise<Array<Object>>} List of matching ward records.
   */
  async getWardsByBlock(hospitalId, blockId) {
    return this.request(`/api/hospitals/${hospitalId}/wards/?block=${blockId}`);
  }

  /**
   * Switch the logged-in user's active ward assignment.
   *
   * @param {number|string} wardId - Target ward identifier.
   * @returns {Promise<Object>} Updated profile state.
   */
  async switchWard(wardId) {
    return this.request('/api/switch-ward/', {
      method: 'POST',
      body: JSON.stringify({ ward_id: wardId }),
    });
  }

  /**
   * Switch the logged-in user's active building block assignment.
   *
   * @param {number|string} blockId - Target block identifier.
   * @returns {Promise<Object>} Updated profile state.
   */
  async switchBlock(blockId) {
    return this.request('/api/switch-block/', {
      method: 'POST',
      body: JSON.stringify({ block_id: blockId }),
    });
  }

  /**
   * Refresh current user profile details, active roles, and tenant status.
   *
   * @returns {Promise<Object>} Current authenticated user representation.
   */
  async refreshUser() {
    return this.request('/api/refresh-user/', {
      method: 'POST',
    });
  }

  /**
   * Create a new hospital ward.
   *
   * @param {number|string} hospitalId - Hospital identifier.
   * @param {Object} wardData - Ward parameters (name, block, floor).
   * @returns {Promise<Object>} Created ward record.
   */
  async createWard(hospitalId, wardData) {
    return this.request(`/api/hospitals/${hospitalId}/wards/`, {
      method: 'POST',
      body: JSON.stringify(wardData),
    });
  }

  /**
   * Update existing ward properties.
   *
   * @param {number|string} hospitalId - Hospital identifier.
   * @param {number|string} wardId - Ward identifier.
   * @param {Object} wardData - Updated attributes.
   * @returns {Promise<Object>} Updated ward record.
   */
  async updateWard(hospitalId, wardId, wardData) {
    return this.request(`/api/hospitals/${hospitalId}/wards/${wardId}/`, {
      method: 'PATCH',
      body: JSON.stringify(wardData),
    });
  }

  /**
   * Delete a hospital ward.
   *
   * @param {number|string} hospitalId - Hospital identifier.
   * @param {number|string} wardId - Ward identifier to delete.
   * @returns {Promise<null>}
   */
  async deleteWard(hospitalId, wardId) {
    return this.request(`/api/hospitals/${hospitalId}/wards/${wardId}/`, {
      method: 'DELETE',
    });
  }

  /**
   * Generate a fresh verification OTP for attendee and completion workflows.
   *
   * @param {number|string} hospitalId - Hospital identifier.
   * @param {string} memoId - Unique memo identifier.
   * @returns {Promise<Object>} Refreshed OTP details.
   */
  async refreshOTP(hospitalId, memoId) {
    return this.request(`/api/${hospitalId}/memos/${memoId}/refresh-otp/`, {
      method: 'POST',
    });
  }

  /**
   * Fetch live aggregation metrics and active memos for dashboard visualization.
   *
   * @param {number|string} hospitalId - Hospital identifier.
   * @param {string} [queryParams=''] - URL query parameters.
   * @returns {Promise<Object>} Dashboard metrics dataset.
   */
  async getDashboardMemos(hospitalId, queryParams = '') {
    return this.request(`/api/${hospitalId}/dashboard-memos/?${queryParams}`, {
      method: 'GET'
    });
  }

  // =============== Feedback & Activity Logging =================

  /**
   * Submit platform user feedback and rating.
   *
   * @param {Object} payload - Rating and comments payload.
   * @returns {Promise<Object>} Created feedback record.
   */
  async createFeedback(payload) {
    return this.request('/api/feedback/create/', {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  /**
   * Retrieve paginated feedback entries submitted by users.
   *
   * @param {number} [page=1] - Target page index.
   * @returns {Promise<Object>} Paginated feedback list response.
   */
  async getFeedbacks(page = 1) {
    return this.request(`/api/feedback/list/?page=${page}`, {
      method: 'GET'
    });
  }

  /**
   * Retrieve paginated audit trail of user activities with search filters.
   *
   * @param {Object} [params={}] - Query filters (page, role, status, user_id).
   * @returns {Promise<Object>} Paginated activity records.
   */
  async getUserActivities(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.page !== undefined && params.page !== null && !isNaN(Number(params.page)) && typeof params.page !== 'boolean') {
      queryParams.append('page', params.page);
    }
    if (params.page_size !== undefined && params.page_size !== null && !isNaN(Number(params.page_size)) && typeof params.page_size !== 'boolean') {
      queryParams.append('page_size', params.page_size);
    }
    if (params.cursor) queryParams.append('cursor', params.cursor);
    if (params.name_search) queryParams.append('name_search', params.name_search);
    if (params.role_search) queryParams.append('role_search', params.role_search);
    if (params.phone_search) queryParams.append('phone_search', params.phone_search);
    if (params.user_id) queryParams.append('user_id', params.user_id);
    if (params.status) queryParams.append('status', params.status);

    const queryString = queryParams.toString();
    const url = queryString ? `/api/activity/?${queryString}` : `/api/activity/`;
    return this.request(url);
  }

  /**
   * Bulk export all filtered user activities as an Excel spreadsheet binary blob.
   *
   * @param {Object} [params={}] - Search and filter parameters.
   * @param {string} [params.name_search] - Substring name or phone search.
   * @param {string} [params.role_search] - User role name filter.
   * @param {string} [params.phone_search] - Phone number substring search.
   * @param {string} [params.user_id] - User identifier or name filter.
   * @param {string} [params.status] - 'active' or 'offline' status filter.
   * @returns {Promise<Blob>} Excel workbook (.xlsx) binary stream.
   */
  async exportUserActivitiesExcel(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.name_search) queryParams.append('name_search', params.name_search);
    if (params.role_search) queryParams.append('role_search', params.role_search);
    if (params.phone_search) queryParams.append('phone_search', params.phone_search);
    if (params.user_id) queryParams.append('user_id', params.user_id);
    if (params.status) queryParams.append('status', params.status);

    const queryString = queryParams.toString();
    const url = queryString ? `/api/activity/export/?${queryString}` : `/api/activity/export/`;
    return this.request(url, { isBlob: true });
  }

  /**
   * Export an individual user's complete chronological activity audit trail as an Excel spreadsheet.
   *
   * @param {number|string} userId - User identifier to export.
   * @returns {Promise<Blob>} Excel workbook (.xlsx) binary stream.
   */
  async exportSingleUserActivityExcel(userId) {
    return this.request(`/api/activity/${userId}/export/`, { isBlob: true });
  }

  /**
   * Fetch aggregate statistics of online/active users and daily actions.
   *
   * @returns {Promise<Object>} Activity statistics metrics.
   */
  async getUserActivityStats() {
    return this.request('/api/activity/stats/');
  }

  /**
   * Retrieve chronological activity trail for an individual user.
   *
   * @param {number|string} userId - User identifier.
   * @returns {Promise<Array<Object>>} List of activity logs.
   */
  async getUserActivity(userId) {
    return this.request(`/api/activity/${userId}/`);
  }

  /**
   * Update delivery or click receipt status for a push notification.
   *
   * @param {string} messageId - Firebase notification message identifier.
   * @param {string} status - New receipt status (e.g. 'DELIVERED', 'OPENED').
   * @param {string} [platform='WEB'] - Client platform name.
   * @param {string|null} [tenantSlug=null] - Hospital tenant schema.
   * @returns {Promise<Object|void>} Update response.
   */
  async updateNotificationStatus(messageId, status, platform = 'WEB', tenantSlug = null) {
    if (!messageId) return;
    const options = {
      method: 'POST',
      body: JSON.stringify({
        message_id: messageId,
        status: status,
        platform: platform,
        tenant_slug: tenantSlug || ''
      })
    };
    if (tenantSlug) {
      options.headers = { 'X-Tenant': tenantSlug };
    }
    try {
      return await this.request('/api/activity/notification-status/', options);
    } catch (err) {
      console.warn('[FCM Tracking] Failed to update notification status:', err);
    }
  }

  /**
   * Fetch paginated Firebase Cloud Messaging delivery logs.
   *
   * @param {Object} [params={}] - Search and filter parameters.
   * @returns {Promise<Object>} Paginated notification logs.
   */
  async getFCMLogs(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.status) queryParams.append('status', params.status);
    if (params.platform) queryParams.append('platform', params.platform);
    if (params.search) queryParams.append('search', params.search);
    if (params.date_from) queryParams.append('date_from', params.date_from);
    if (params.date_to) queryParams.append('date_to', params.date_to);

    const queryString = queryParams.toString();
    const url = queryString ? `/api/activity/fcm-logs/?${queryString}` : `/api/activity/fcm-logs/`;
    return this.request(url);
  }

  /**
   * Fetch paginated SMS/OTP delivery audit logs.
   *
   * @param {Object} [params={}] - Filter parameters (status, purpose, phone).
   * @returns {Promise<Object>} Paginated OTP dispatch records.
   */
  async getOTPLogs(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.status) queryParams.append('status', params.status);
    if (params.purpose) queryParams.append('purpose', params.purpose);
    if (params.delivery_status) queryParams.append('delivery_status', params.delivery_status);
    if (params.search) queryParams.append('search', params.search);
    if (params.date_from) queryParams.append('date_from', params.date_from);
    if (params.date_to) queryParams.append('date_to', params.date_to);

    const queryString = queryParams.toString();
    const url = queryString ? `/api/activity/otp-logs/?${queryString}` : `/api/activity/otp-logs/`;
    return this.request(url);
  }

  /**
   * Export filtered FCM push notification dispatch logs as an Excel spreadsheet binary blob.
   *
   * @param {Object} [params={}] - Filter parameters (status, platform, search, date_from, date_to).
   * @returns {Promise<Blob>} Excel workbook (.xlsx) binary stream.
   */
  async exportFCMLogsExcel(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    if (params.platform) queryParams.append('platform', params.platform);
    if (params.search) queryParams.append('search', params.search);
    if (params.date_from) queryParams.append('date_from', params.date_from);
    if (params.date_to) queryParams.append('date_to', params.date_to);

    const queryString = queryParams.toString();
    const url = queryString ? `/api/activity/fcm-logs/export/?${queryString}` : `/api/activity/fcm-logs/export/`;
    return this.request(url, { isBlob: true });
  }

  /**
   * Export filtered SMS OTP verification logs as an Excel spreadsheet binary blob.
   *
   * @param {Object} [params={}] - Filter parameters (status, purpose, delivery_status, search, date_from, date_to).
   * @returns {Promise<Blob>} Excel workbook (.xlsx) binary stream.
   */
  async exportOTPLogsExcel(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    if (params.purpose) queryParams.append('purpose', params.purpose);
    if (params.delivery_status) queryParams.append('delivery_status', params.delivery_status);
    if (params.search) queryParams.append('search', params.search);
    if (params.date_from) queryParams.append('date_from', params.date_from);
    if (params.date_to) queryParams.append('date_to', params.date_to);

    const queryString = queryParams.toString();
    const url = queryString ? `/api/activity/otp-logs/export/?${queryString}` : `/api/activity/otp-logs/export/`;
    return this.request(url, { isBlob: true });
  }
}

export default ApiService;
